import { useEffect, useState, useRef } from 'react';

interface UsePolledDataOptions {
  /**
   * Optional function to transform error messages into user-friendly
   * ones. Receives the raw error as a second argument so callers can
   * branch on `ApiError.code` instead of matching translated text.
   */
  errorMapper?: (message: string, error?: unknown) => string | null;
}

interface UsePolledDataResult<T> {
  data: T | null;
  message: string | null;
  loading: boolean;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 5000;

/**
 * Generic hook for fetching data with polling.
 *
 * - Refetches every 5 seconds while the tab is visible.
 * - Pauses when `document.visibilityState === 'hidden'` (no timer, no fetches).
 * - Refetches immediately on visibilitychange→visible so the user never sees
 *   stale data while waiting for the next interval tick.
 * - Aborts in-flight fetches on dep change, unmount, or hide. The aborted
 *   fetch's result is discarded — fixes the latent race where a slow previous
 *   response could land after a newer one. Consumers that pass `signal`
 *   through to `fetch()` get real network cancellation; the rest still get
 *   correct ordering for free.
 *
 * @param fetchFn  - Async function that returns the data. Receives an
 *                   AbortSignal that fires when this invocation is superseded.
 *                   Should throw on error.
 * @param deps     - Dependency array that triggers re-fetching (e.g. [slug]).
 * @param options  - Optional configuration (errorMapper).
 */
export function usePolledData<T>(
  fetchFn: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options?: UsePolledDataOptions,
): UsePolledDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const dataRef = useRef<T | null>(null);
  const refetchRef = useRef<() => void>(() => {});

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    let activeController: AbortController | null = null;

    const abortActive = () => {
      if (activeController) {
        activeController.abort();
        activeController = null;
      }
    };

    const doFetchOnce = async () => {
      abortActive();
      const controller = new AbortController();
      activeController = controller;
      try {
        const result = await fetchFn(controller.signal);
        if (controller.signal.aborted) return;
        setData(result);
        dataRef.current = result;
        setMessage(null);
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load data';
        if (options?.errorMapper) {
          const mapped = options.errorMapper(msg, err);
          if (mapped !== null) {
            setMessage(mapped);
          } else if (!dataRef.current) {
            setMessage(msg);
          }
        } else if (!dataRef.current) {
          setMessage(msg);
        }
      } finally {
        if (controller.signal.aborted) return;
        if (activeController === controller) activeController = null;
        setLoading(false);
      }
    };

    const startInterval = () => {
      if (interval !== null) return;
      interval = setInterval(doFetchOnce, POLL_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        abortActive();
        stopInterval();
      } else {
        doFetchOnce();
        startInterval();
      }
    };

    refetchRef.current = doFetchOnce;
    setLoading(true);
    doFetchOnce();
    if (document.visibilityState === 'visible') startInterval();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopInterval();
      abortActive();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, message, loading, refetch: () => refetchRef.current() };
}
